import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { applyUserLanguage } from '../i18n';
import { ReactComponent as DreamLogo } from '../icons/dream-onboarding-logo.svg';
import { ReactComponent as GoogleLogo } from '../icons/google-logo.svg';
import outlookLogo from '../icons/outlook.png';
import './onboarding-connect-page.scss';

type ConnectedAccount = {
  id: number;
  identifier: string | null;
  type?: string;
};

type AccountsPayload = {
  displayIdentity: string;
  whatsappNumberDigits: string;
  returnToWhatsAppUrl: string;
  accounts: ConnectedAccount[];
  language?: string | null;
};

type BannerTone = 'success' | 'warning' | 'error' | 'info';

type BannerState = {
  tone: BannerTone;
  message: string;
};

const getStatusBanner = (
  status: string | null,
  t: (key: string) => string,
): BannerState | null => {
  switch (status) {
    case 'already-connected':
      return { tone: 'info', message: t('connect.statusAlreadyConnected') };
    case 'expired':
      return { tone: 'warning', message: t('connect.statusExpired') };
    case 'invalid-token':
      return { tone: 'error', message: t('connect.statusInvalidToken') };
    case 'already-linked-other':
      return {
        tone: 'warning',
        message: t('connect.statusAlreadyLinkedOther'),
      };
    case 'error':
      return { tone: 'error', message: t('connect.statusError') };
    default:
      return null;
  }
};

const EllipseGlow = () => (
  <svg
    className='onboarding-connect__ellipse-svg'
    preserveAspectRatio='none'
    viewBox='0 0 736 276'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <g filter='url(#onboarding_ellipse_blur)'>
      <path
        d='M368 228C544.731 228 688 147.411 688 48H48C48 147.411 191.269 228 368 228Z'
        fill='url(#onboarding_ellipse_gradient)'
      />
      <path
        d='M368 228C544.731 228 688 147.411 688 48H48C48 147.411 191.269 228 368 228Z'
        fill='#FFFEFC'
        fillOpacity='0.75'
      />
    </g>
    <defs>
      <filter
        id='onboarding_ellipse_blur'
        x='0'
        y='0'
        width='736'
        height='276'
        filterUnits='userSpaceOnUse'
        colorInterpolationFilters='sRGB'
      >
        <feFlood floodOpacity='0' result='BackgroundImageFix' />
        <feBlend
          mode='normal'
          in='SourceGraphic'
          in2='BackgroundImageFix'
          result='shape'
        />
        <feGaussianBlur stdDeviation='24' result='effect1_foregroundBlur' />
      </filter>
      <linearGradient
        id='onboarding_ellipse_gradient'
        x1='468.662'
        y1='48'
        x2='245.91'
        y2='166.778'
        gradientUnits='userSpaceOnUse'
      >
        <stop stopColor='#F7F3ED' />
        <stop offset='0.33' stopColor='#97D5FC' />
        <stop offset='0.66' stopColor='#F69DB0' />
        <stop offset='1' stopColor='#FFD601' />
      </linearGradient>
    </defs>
  </svg>
);

const MinusCircleIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' aria-hidden='true'>
    <circle
      cx='12'
      cy='12'
      r='9'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    />
    <path
      d='M8 12h8'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
  </svg>
);

const OnboardingConnectPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const status = searchParams.get('status');
  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<ConnectedAccount[]>([]);
  const [displayIdentity, setDisplayIdentity] = useState('');
  const [returnToWhatsAppUrl, setReturnToWhatsAppUrl] = useState('');
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [busyAccountId, setBusyAccountId] = useState<number | null>(null);

  const connectUrl = useMemo(() => {
    if (!token) {
      return '';
    }

    return `${apiBase}/wa-onboarding/connect?token=${encodeURIComponent(token)}`;
  }, [apiBase, token]);

  const connectMicrosoftUrl = useMemo(() => {
    if (!token) {
      return '';
    }

    return `${apiBase}/wa-onboarding/connect-microsoft?token=${encodeURIComponent(token)}`;
  }, [apiBase, token]);

  const statusBanner = useMemo(() => getStatusBanner(status, t), [status, t]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setFetchError(t('connect.missingToken'));
      return;
    }

    let cancelled = false;

    const loadAccounts = async () => {
      setLoading(true);
      setFetchError(null);

      try {
        const response = await fetch(
          `${apiBase}/wa-onboarding/accounts?token=${encodeURIComponent(token)}`,
        );
        const payload = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(
            payload?.error ||
              payload?.message ||
              t('connect.loadAccountsError'),
          );
        }

        if (cancelled) {
          return;
        }

        const data = payload?.data as AccountsPayload | undefined;
        applyUserLanguage(data?.language);
        setAccounts(data?.accounts || []);
        setDisplayIdentity(data?.displayIdentity || '');
        setReturnToWhatsAppUrl(data?.returnToWhatsAppUrl || '');
      } catch (error) {
        if (!cancelled) {
          setFetchError(
            error instanceof Error
              ? error.message
              : t('connect.loadAccountsError'),
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAccounts();

    return () => {
      cancelled = true;
    };
  }, [apiBase, token]);

  const handleDisconnect = async (oauthId: number) => {
    if (!token) {
      return;
    }

    setBusyAccountId(oauthId);

    try {
      const response = await fetch(
        `${apiBase}/wa-onboarding/accounts/disconnect`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token, oauthId }),
        },
      );

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          payload?.error || payload?.message || t('connect.disconnectError'),
        );
      }

      const data = payload?.data as AccountsPayload | undefined;
      setAccounts(data?.accounts || []);
      setDisplayIdentity(data?.displayIdentity || '');
      setReturnToWhatsAppUrl(data?.returnToWhatsAppUrl || '');
    } catch (error) {
      setFetchError(
        error instanceof Error ? error.message : t('connect.disconnectError'),
      );
    } finally {
      setBusyAccountId(null);
    }
  };

  const goToConnect = () => {
    if (connectUrl) {
      window.location.assign(connectUrl);
    }
  };

  const goToConnectMicrosoft = () => {
    if (connectMicrosoftUrl) {
      window.location.assign(connectMicrosoftUrl);
    }
  };

  const returnToWhatsAppLink = returnToWhatsAppUrl || '';
  const hasAccounts = accounts.length > 0;

  return (
    <div className='onboarding-connect'>
      <div className='onboarding-connect__inner'>
        <header className='onboarding-connect__header'>
          <span className='onboarding-connect__ellipse' aria-hidden='true'>
            <EllipseGlow />
          </span>

          <DreamLogo className='onboarding-connect__logo' />

          <div className='onboarding-connect__identity'>
            <span className='onboarding-connect__identity-text'>
              {displayIdentity || t('connect.yourAccount')}
            </span>
          </div>
        </header>

        <main className='onboarding-connect__main'>
          {statusBanner || fetchError ? (
            <div
              className={`onboarding-connect__status onboarding-connect__status--${
                fetchError ? 'error' : statusBanner?.tone || 'info'
              }`}
            >
              {fetchError || statusBanner?.message}
            </div>
          ) : null}

          <h1 className='onboarding-connect__title'>{t('connect.title')}</h1>

          <div className='onboarding-connect__body'>
            <p>{t('connect.body1')}</p>
            <p>{t('connect.body2')}</p>
          </div>

          {hasAccounts && (
            <div className='onboarding-connect__done-banner'>
              <span className='onboarding-connect__done-strong'>
                {t('connect.allDone')}
              </span>{' '}
              {t('connect.youCan')}{' '}
              {returnToWhatsAppLink ? (
                <a
                  className='onboarding-connect__done-link'
                  href={returnToWhatsAppLink}
                  target='_blank'
                  rel='noreferrer'
                >
                  {t('connect.returnToWhatsApp')}
                </a>
              ) : (
                <span className='onboarding-connect__done-link'>
                  {t('connect.returnToWhatsApp')}
                </span>
              )}
              {t('connect.keepConnecting')}
            </div>
          )}

          <div className='onboarding-connect__card'>
            {loading ? (
              <div className='onboarding-connect__loading'>
                {t('connect.loadingAccounts')}
              </div>
            ) : (
              <>
                {accounts.map((account) => (
                  <div
                    key={account.id}
                    className='onboarding-connect__account-row'
                  >
                    <span className='onboarding-connect__account-email'>
                      {account.identifier || t('connect.connectedAccount')}
                    </span>
                    <button
                      type='button'
                      className='onboarding-connect__account-remove'
                      onClick={() => handleDisconnect(account.id)}
                      disabled={busyAccountId === account.id}
                      aria-label={t('connect.disconnectAria', {
                        account: account.identifier || t('connect.thisAccount'),
                      })}
                    >
                      <MinusCircleIcon />
                    </button>
                  </div>
                ))}

                <button
                  type='button'
                  className={`onboarding-connect__connect-button${
                    hasAccounts
                      ? ' onboarding-connect__connect-button--outline'
                      : ''
                  }`}
                  onClick={goToConnect}
                  disabled={!connectUrl}
                >
                  <GoogleLogo className='onboarding-connect__google-glyph' />
                  <span>
                    {hasAccounts
                      ? t('connect.connectAnotherGoogle')
                      : t('connect.connectGoogle')}
                  </span>
                </button>

                <button
                  type='button'
                  className={`onboarding-connect__connect-button${
                    hasAccounts
                      ? ' onboarding-connect__connect-button--outline'
                      : ''
                  }`}
                  onClick={goToConnectMicrosoft}
                  disabled={!connectMicrosoftUrl}
                >
                  <img
                    className='onboarding-connect__google-glyph'
                    src={outlookLogo}
                    alt=''
                    aria-hidden='true'
                  />
                  <span>
                    {hasAccounts
                      ? t('connect.connectAnotherOutlook')
                      : t('connect.connectOutlook')}
                  </span>
                </button>

                <p className='onboarding-connect__disclaimer'>
                  {t('connect.disclaimer')}
                </p>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default OnboardingConnectPage;
